import { useMemo } from 'react';
import {
  useForecastComparisonPeriod,
  useForecastPeriod,
  useLatestForecastModel
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/hooks';
import { useKeyMetricForecastSummaryData } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useKeyMetricForecastSummaryData';
import { useMetricByWeekIdV2, UseMetricByWeekIdV2Args } from 'src/components/BeaconV2Components/SplineChartV2';
import { SplineChartV2Series, SplineChartV2DataPoint } from 'src/components/BeaconV2Components/SplineChartV2';
import { calculateMetricTotal } from 'src/components/BeaconV2Components/SplineChartV2/metricTotalUtils';
import {
  ForecastComparisonPeriod,
  ForecastPeriod
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/types';
import { useMetricByWeekIdArgsForForecast } from './useForecastMetricByWeekId';
import { timestampToWeekId } from 'src/utils/dateUtils';
import { useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';

/**
 * Converts V1 forecast data (number[][]) to V2 format (SplineChartV2DataPoint[])
 */
function convertForecastDataToV2(forecastData: number[][]): SplineChartV2DataPoint[] {
  return forecastData.map(([timestamp, value]) => ({
    weekId: timestampToWeekId(timestamp),
    value: value ?? null
  }));
}

/**
 * Determines the forecast data to display based on period and comparison settings.
 * For FULL_YEAR: connects forecast to the end of primary (YTD) data
 * For PRIOR_PERIOD comparison: connects forecast to the end of comparison data
 */
function useDisplayedForecastDataV2({
  primaryData,
  comparisonData,
  forecastData
}: {
  primaryData: SplineChartV2DataPoint[];
  comparisonData: SplineChartV2DataPoint[];
  forecastData: SplineChartV2DataPoint[];
}): SplineChartV2DataPoint[] {
  const forecastPeriod = useForecastPeriod();
  const forecastComparison = useForecastComparisonPeriod();

  return useMemo(() => {
    if (forecastPeriod === ForecastPeriod.FULL_YEAR && primaryData.length > 0) {
      // Connect forecast to the last point of primary data
      const lastPrimaryPoint = primaryData[primaryData.length - 1];
      // Check if forecast already starts with the same week (avoid duplicate)
      if (forecastData.length > 0 && forecastData[0].weekId === lastPrimaryPoint.weekId) {
        // Replace the first forecast point's value with the connection point value
        return [lastPrimaryPoint, ...forecastData.slice(1)];
      }
      return [lastPrimaryPoint, ...forecastData];
    }
    if (forecastComparison === ForecastComparisonPeriod.PRIOR_PERIOD && comparisonData.length > 0) {
      // Connect forecast to the last point of comparison data
      const lastComparisonPoint = comparisonData[comparisonData.length - 1];
      // Check if forecast already starts with the same week (avoid duplicate)
      if (forecastData.length > 0 && forecastData[0].weekId === lastComparisonPoint.weekId) {
        // Replace the first forecast point's value with the connection point value
        return [lastComparisonPoint, ...forecastData.slice(1)];
      }
      return [lastComparisonPoint, ...forecastData];
    }
    return forecastData;
  }, [forecastComparison, forecastData, forecastPeriod, primaryData, comparisonData]);
}

export interface UseForecastMetricByWeekIdV2Args extends Omit<UseMetricByWeekIdV2Args, 'requestBuilder' | 'primaryAggregationBuilder' | 'secondaryAggregationBuilder'> {
  forecastPeriodOverride?: ForecastPeriod;
  useKeyMetricForecastSummaryDataArgs?: Parameters<typeof useKeyMetricForecastSummaryData>[0];
  /**
   * Override the aggregation function used for calculating totals.
   * For example, 'avg' for contentScore which should be averaged, not summed.
   */
  aggregationFunctionForTotal?: string;
}

export interface UseForecastMetricByWeekIdV2Result {
  // Series data ready for SplineChartV2
  primarySeries: SplineChartV2Series;
  comparisonSeries: SplineChartV2Series;
  forecastSeries: SplineChartV2Series;

  // Raw data points
  primaryData: SplineChartV2DataPoint[];
  comparisonData: SplineChartV2DataPoint[];
  forecastData: SplineChartV2DataPoint[];

  // Formatted totals
  formattedPrimaryTotal: string;
  formattedComparisonTotal: string;
  formattedPercentChange: string;

  // Raw values
  percentChange: number | null;

  // Loading state
  isLoading: boolean;
}

/**
 * V2 version of useForecastMetricByWeekId that returns data in SplineChartV2 format.
 * Combines YTD/historic data with forecast projections.
 */
export default function useForecastMetricByWeekIdV2({
  useKeyMetricForecastSummaryDataArgs,
  forecastPeriodOverride,
  aggregationFunctionForTotal,
  ...args
}: UseForecastMetricByWeekIdV2Args): UseForecastMetricByWeekIdV2Result {
  const { loading: modelLoading } = useLatestForecastModel();
  const { getSplineData, isLoading: forecastLoading } = useKeyMetricForecastSummaryData(useKeyMetricForecastSummaryDataArgs);
  const forecastPeriod = useForecastPeriod();
  const formatMetric = useMetricFormatter();

  // Get the request builder args for forecast time periods
  const metricByWeekIdArgs = useMetricByWeekIdArgsForForecast();

  // Determine the aggregation function override (e.g., 'avg' for contentScore)
  const effectiveAggregationFunctionOverride = aggregationFunctionForTotal ?? 
    (args.fieldName === 'contentScore' ? 'avg' : undefined);

  // Use the V2 hook to get primary and comparison data
  const metricByValuesV2 = useMetricByWeekIdV2({
    ...metricByWeekIdArgs,
    ...args,
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch: !modelLoading,
      ...args.useGenericAdvancedSearchArgs
    }
  });

  // Convert raw forecast data to V2 format
  const rawForecastData = useMemo(() => {
    const splineData = getSplineData(args.indexName, args.fieldName);
    return convertForecastDataToV2(splineData);
  }, [getSplineData, args.indexName, args.fieldName]);

  // Get the displayed forecast data (connected to primary or comparison)
  const displayedForecastData = useDisplayedForecastDataV2({
    primaryData: metricByValuesV2.primaryData,
    comparisonData: metricByValuesV2.comparisonData,
    forecastData: rawForecastData
  });

  // Determine primary data based on forecast period
  const effectiveForecastPeriod = forecastPeriodOverride ?? forecastPeriod;
  const primaryData = useMemo(
    () => effectiveForecastPeriod === ForecastPeriod.FULL_YEAR 
      ? metricByValuesV2.primaryData 
      : [],
    [effectiveForecastPeriod, metricByValuesV2.primaryData]
  );

  // Calculate totals for forecast periods
  // For FULL_YEAR: combined total (actual YTD + forecast) vs full comparison year
  // For non-FULL_YEAR: forecast total vs full comparison data (no filtering by week IDs)
  const { formattedPrimaryTotal, formattedComparisonTotal, percentChange, formattedPercentChange } = useMemo(() => {
    const isPercentType = metricByValuesV2.metricType === METRICTYPE.PERCENT;
    
    if (effectiveForecastPeriod === ForecastPeriod.FULL_YEAR) {
      // Combine actual YTD data with forecast data for total calculation
      // This properly handles different aggregation types (sum, avg, stats, cardinality, lastValue)
      const combinedData = [...metricByValuesV2.primaryData, ...rawForecastData];
      const combinedTotal = calculateMetricTotal(combinedData, metricByValuesV2.field, {
        aggregationFunctionOverride: effectiveAggregationFunctionOverride
      });
      
      // For forecasts, calculate comparison total from ALL comparison data (full prior year)
      // not the filtered subset that useMetricByWeekIdV2 returns
      const fullComparisonTotal = calculateMetricTotal(metricByValuesV2.comparisonData, metricByValuesV2.field, {
        aggregationFunctionOverride: effectiveAggregationFunctionOverride
      });
      
      // Calculate percent change
      const pctChange = fullComparisonTotal !== 0 
        ? ((combinedTotal - fullComparisonTotal) / fullComparisonTotal) * 100 
        : null;
      
      // Format the combined total using the metric type from metricByValuesV2
      // Use showFullValue: false to get shortened format for large numbers (e.g., 22.21M)
      // but showFullValue: true for percentages to show the full percentage value
      const formattedTotal = formatMetric(
        combinedTotal,
        metricByValuesV2.metricType,
        { showFullValue: isPercentType, decimalPlaces: 2 }
      );
      
      // Format the full comparison total
      const formattedComparison = formatMetric(
        fullComparisonTotal,
        metricByValuesV2.metricType,
        { showFullValue: isPercentType, decimalPlaces: 2 }
      );
      
      const formattedPctChange = pctChange !== null 
        ? `${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(1)}%`
        : '--';
      
      return {
        formattedPrimaryTotal: formattedTotal,
        formattedComparisonTotal: formattedComparison,
        percentChange: pctChange,
        formattedPercentChange: formattedPctChange
      };
    }
    
    // For non-FULL_YEAR periods (Next 52W, Next 26W, etc.):
    // - Primary total is from FORECAST data only (not YTD data)
    // - Comparison total is from ALL comparison data (no week ID filtering)
    // This matches V1 behavior with buildDataSetForMetricTotal and customFilterComparisonForTotal
    const forecastTotal = calculateMetricTotal(rawForecastData, metricByValuesV2.field, {
      aggregationFunctionOverride: effectiveAggregationFunctionOverride
    });
    
    // Calculate comparison total from ALL comparison data
    // Don't filter by week IDs - use the full comparison period that was fetched
    const fullComparisonTotal = calculateMetricTotal(metricByValuesV2.comparisonData, metricByValuesV2.field, {
      aggregationFunctionOverride: effectiveAggregationFunctionOverride
    });
    
    // Calculate percent change
    const pctChange = fullComparisonTotal !== 0 
      ? ((forecastTotal - fullComparisonTotal) / fullComparisonTotal) * 100 
      : null;
    
    const formattedTotal = formatMetric(
      forecastTotal,
      metricByValuesV2.metricType,
      { showFullValue: isPercentType, decimalPlaces: 2 }
    );
    
    const formattedComparison = formatMetric(
      fullComparisonTotal,
      metricByValuesV2.metricType,
      { showFullValue: isPercentType, decimalPlaces: 2 }
    );
    
    const formattedPctChange = pctChange !== null 
      ? `${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(1)}%`
      : '--';
    
    return {
      formattedPrimaryTotal: formattedTotal,
      formattedComparisonTotal: formattedComparison,
      percentChange: pctChange,
      formattedPercentChange: formattedPctChange
    };
  }, [
    effectiveForecastPeriod,
    effectiveAggregationFunctionOverride,
    metricByValuesV2.primaryData,
    metricByValuesV2.comparisonData,
    metricByValuesV2.field,
    metricByValuesV2.metricType,
    rawForecastData,
    formatMetric
  ]);

  // Build series for SplineChartV2
  const primarySeries: SplineChartV2Series = useMemo(() => ({
    id: `${args.indexName}-${args.fieldName}-primary`,
    name: 'Primary',
    data: primaryData
  }), [args.indexName, args.fieldName, primaryData]);

  const comparisonSeries: SplineChartV2Series = useMemo(() => ({
    id: `${args.indexName}-${args.fieldName}-comparison`,
    name: 'Comparison',
    data: metricByValuesV2.comparisonData
  }), [args.indexName, args.fieldName, metricByValuesV2.comparisonData]);

  const forecastSeries: SplineChartV2Series = useMemo(() => ({
    id: `${args.indexName}-${args.fieldName}-forecast`,
    name: 'Forecast',
    data: displayedForecastData,
    isDashed: true
  }), [args.indexName, args.fieldName, displayedForecastData]);

  return {
    primarySeries,
    comparisonSeries,
    forecastSeries,
    primaryData,
    comparisonData: metricByValuesV2.comparisonData,
    forecastData: displayedForecastData,
    formattedPrimaryTotal,
    formattedComparisonTotal,
    formattedPercentChange,
    percentChange,
    isLoading: modelLoading || forecastLoading || metricByValuesV2.isLoading
  };
}
